import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { fmtCurrency } from "../lib/formatters";
import { DelayedTooltipContent } from "../lib/DelayedTooltip";
import { useAutoDismiss } from "../hooks/useAutoDismiss";

// Step 13 · Chart 2 · Spending Mix (Dave 2026-08-05 spec)
//
// Donut chart of expenditure share by element over the current horizon.
// Slices under 5% collapse into a single "Other" wedge; the sub-5%
// elements are itemized in a right-side column so nothing hides.
//
// • Center hole shows total spending across the horizon
// • Legend row (below donut): "Element name — 12.3% — $1,234,567"
// • Right column: Other Breakdown (name · % · $)
// • Horizon context sensitive via `calc.expenditure_calendar` slice.

// Palette — kept in-file so the chart is self-contained. Uses the app's
// existing color anchors so slice colors read as "part of the family".
const SLICE_COLORS = [
  "#1C4E80", // navy
  "#88BFDE", // sky
  "#EA6A47", // coral
  "#F4D35E", // yellow
  "#83C88F", // green
  "#7B6FCA", // lavender
  "#3A7FBD", // mid-blue
  "#D98E5A", // muted orange
];
const OTHER_COLOR = "#9AA9B4"; // slate-gray — matches ActivityChart's adjustment stack
// 2026-08-06 (Dave, revert of the "10 smallest" pass) — Other aggregation
// is back to a percent threshold: any element that individually represents
// LESS than OTHER_THRESHOLD_PCT of total spending rolls up into the
// "Other" donut wedge, with each rolled-up element itemized in the right
// column. The explosion affordance (pie rotated so Other lands at 3
// o'clock + two dashed connector lines) is preserved regardless of
// aggregation rule.
const OTHER_THRESHOLD_PCT = 4;
const PANEL_DONUT_LEFT = 16;
const PANEL_DONUT_TOP = 2;

// Rotating palette for the "Other" stacked-column segments. Cycles the same
// hues used by main slices so segments read as "part of the mix", not a
// separate visual language. Repeats after 8 items.
const OTHER_SEG_COLORS = [
  "#5A6B75",
  "#7B8797",
  "#88BFDE",
  "#83C88F",
  "#F4D35E",
  "#D98E5A",
  "#7B6FCA",
  "#3A7FBD",
];

function fmtPct1(v) {
  return `${v.toFixed(1)}%`;
}

// Click-in popup listing what "Other" contains (panel variant).
function OtherPopup({ items, total, byGroup, onClose, title = "Other Breakdown", subtitle, testid = "spending-mix-other-popup" }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const noun = byGroup ? (items.length === 1 ? "group" : "groups") : (items.length === 1 ? "element" : "elements");
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center" style={{ background: "rgba(255,255,255,0.6)" }} onClick={onClose}>
      <div
        className="bg-white border border-navy shadow-xl rounded-[6px] flex flex-col"
        style={{ width: "86%", maxWidth: 560, maxHeight: "86%" }}
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-rule">
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-navy truncate">{title}</div>
          <button type="button" onClick={onClose} className="text-slate hover:text-coral" data-testid={`${testid}-close`}>
            <X size={14} />
          </button>
        </div>
        <div className="px-3 pt-1.5 pb-1 text-[9.5px] text-slate italic">
          {subtitle || <>{byGroup ? "Groups" : "Elements"} under {OTHER_THRESHOLD_PCT}% of total spending · {items.length} {noun} · {fmtCurrency(total)} total</>}
        </div>
        <div className="overflow-auto px-3 pb-2">
          {items.map((it, idx) => (
            <div key={it.name} className="flex items-center gap-2 text-[10.5px] text-ink border-b border-rule/70 py-1" data-testid={`${testid}-item-${idx}`}>
              <span style={{ width: 8, height: 8, background: OTHER_SEG_COLORS[idx % OTHER_SEG_COLORS.length], flexShrink: 0 }} />
              <span className="flex-1 truncate" title={it.name}>{it.name}</span>
              <span className="font-mono tabular-nums text-slate w-[40px] text-right">{fmtPct1(it.pct)}</span>
              <span className="font-mono tabular-nums font-semibold w-[72px] text-right">{fmtCurrency(it.amount)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SpendingMixChart({
  open: openProp,
  onClose,
  calc,
  association,
  horizon,
  variant = "modal", // "panel" = inline Illustrations-tab panel (Dave 2026-10-12)
  autoDismiss = true,
}) {
  const isPanel = variant === "panel";
  const open = isPanel || openProp;
  const modalOpen = !isPanel && openProp;
  const [qcLensTop, setQcLensTop] = useState(0);
  const [otherOpen, setOtherOpen] = useState(false);
  const [drillGroup, setDrillGroup] = useState(null);

  // Refs + geometry state for the "explosion" connector lines that link
  // the Other wedge (rotated to 3 o'clock) to the top and bottom of the
  // stacked column on the right. Recomputed whenever the modal opens or
  // the container/stack are resized.
  const bodyRef = useRef(null);
  const donutRef = useRef(null);
  const stackRef = useRef(null);
  const [connectorGeom, setConnectorGeom] = useState(null);

  // 2026-02-19 (Dave) — auto-dismiss after `hover_visible_seconds`.
  useAutoDismiss(modalOpen && autoDismiss, onClose);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !drillGroup) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modalOpen, onClose, drillGroup]);

  // iPhone QC lens fix — anchor the absolute-positioned overlay at the
  // current scroll position on open (see FundingLevelAnalysisChart for
  // the full explanation). Detection via the `.qc-lens-active` class on
  // <html> instead of matchMedia, which stops matching once the QC lens
  // hack rewrites the viewport meta to `width=1440`.
  useEffect(() => {
    if (!modalOpen) return;
    if (document.documentElement.classList.contains("qc-lens-active")) {
      setQcLensTop(window.scrollY || 0);
    }
  }, [modalOpen]);

  // Aggregate spending per element within the horizon. Runs unconditionally
  // to keep hook order stable across renders (open=false is handled below).
  const { mainSlices, otherItems, otherTotal, grandTotal, byGroup, groupElements } = useMemo(() => {
    // Elements-table Groups (category) drive the slices when any are assigned.
    const catByName = new Map(
      (association?.elements || []).map((e) => [e.name, (e.category || "").trim()]),
    );
    const useGroups = true;
    const labelOf = (name) => catByName.get(name) || "Uncategorized";
    const totals = new Map();
    const elTotals = new Map();
    (calc?.expenditure_calendar || [])
      .filter((entry) => entry.year_index <= horizon)
      .forEach((entry) => {
        (entry.elements || []).forEach((el) => {
          const key = labelOf(el.name);
          totals.set(key, (totals.get(key) || 0) + (el.amount || 0));
          if (!elTotals.has(key)) elTotals.set(key, new Map());
          const m = elTotals.get(key);
          m.set(el.name, (m.get(el.name) || 0) + (el.amount || 0));
        });
      });
    const grand = Array.from(totals.values()).reduce((s, v) => s + v, 0);
    const items = Array.from(totals.entries())
      .map(([name, amount]) => ({
        name,
        amount,
        pct: grand > 0 ? (amount / grand) * 100 : 0,
      }))
      .sort((a, b) => b.amount - a.amount);
    const main = items.filter((it) => it.pct >= OTHER_THRESHOLD_PCT);
    const other = items.filter((it) => it.pct < OTHER_THRESHOLD_PCT);
    const otherSum = other.reduce((s, it) => s + it.amount, 0);
    const groupEls = new Map();
    elTotals.forEach((m, g) => {
      const gTotal = totals.get(g) || 0;
      groupEls.set(g, Array.from(m.entries())
        .map(([name, amount]) => ({ name, amount, pct: gTotal > 0 ? (amount / gTotal) * 100 : 0 }))
        .sort((a, b) => b.amount - a.amount));
    });
    return { mainSlices: main, otherItems: other, otherTotal: otherSum, grandTotal: grand, byGroup: useGroups, groupElements: groupEls };
  }, [calc, horizon, association]);

  // Rotate the pie so the "Other" wedge is centered at 3 o'clock, pointing
  // right toward the Other Breakdown stacked column. Computed here (before
  // the useLayoutEffect that references it) so the hook order stays stable
  // regardless of the open/calc guard below.
  const otherAngleDeg = grandTotal > 0 && otherTotal > 0
    ? (otherTotal / grandTotal) * 360
    : 0;
  const pieStartAngle = 360 - otherAngleDeg / 2;
  const pieEndAngle = -otherAngleDeg / 2;

  // Compute the connector-line endpoints when the modal is open and
  // whenever the donut or stack containers resize. The donut geometry is
  // derived from the container's inner rect + Recharts' 88% outerRadius
  // convention, and the Other wedge is rotated to 3 o'clock so its two
  // edges land at ±angleO/2 above and below horizontal.
  const otherNameCh = Math.max(8, ...otherItems.map((it) => it.name.length));

  // Panel: largest donut (top-left anchored) that clears the bottom-right legend by 8px.
  const legendRef = useRef(null);
  const [panelDonutPx, setPanelDonutPx] = useState(200);
  useLayoutEffect(() => {
    if (!isPanel) return undefined;
    const bodyEl = bodyRef.current;
    const legEl = legendRef.current;
    if (!bodyEl || !legEl) return undefined;
    const fit = () => {
      const b = bodyEl.getBoundingClientRect();
      const l = legEl.getBoundingClientRect();
      const x0 = PANEL_DONUT_LEFT, y0 = PANEL_DONUT_TOP, pad = 8;
      const L = l.left - b.left - pad, T = l.top - b.top - pad, R = l.right - b.left + pad, B = l.bottom - b.top + pad;
      const clears = (S) => {
        const r = (S / 2) * 0.97, cx = x0 + S / 2, cy = y0 + S / 2;
        const px = Math.min(Math.max(cx, L), R), py = Math.min(Math.max(cy, T), B);
        return Math.hypot(cx - px, cy - py) >= r;
      };
      let lo = 80, hi = Math.max(80, Math.min(b.width - x0 - 4, b.height - y0 - 4));
      while (hi - lo > 1) { const m = (lo + hi) / 2; if (clears(m)) lo = m; else hi = m; }
      setPanelDonutPx(Math.floor(lo));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(bodyEl);
    ro.observe(legEl);
    return () => ro.disconnect();
  }, [isPanel, mainSlices.length, otherItems.length]);

  useLayoutEffect(() => {
    if (!open) return;
    const donutEl = donutRef.current;
    const stackEl = stackRef.current;
    const bodyEl = bodyRef.current;
    if (!donutEl || !stackEl || !bodyEl || otherAngleDeg <= 0) {
      setConnectorGeom(null);
      return;
    }
    const compute = () => {
      const bRect = bodyEl.getBoundingClientRect();
      const dRect = donutEl.getBoundingClientRect();
      const sRect = stackEl.getBoundingClientRect();
      const donutCx = dRect.left + dRect.width / 2 - bRect.left;
      const donutCy = dRect.top + dRect.height / 2 - bRect.top;
      const outerR = Math.min(dRect.width, dRect.height) / 2 * 0.88;
      // Half-angle clamped so an extreme "everything is tail" scenario
      // doesn't blow the geometry.
      const halfRad = Math.max(0.02, Math.min(Math.PI / 2, (otherAngleDeg / 2) * Math.PI / 180));
      // Recharts angles measured from +x axis, counterclockwise positive.
      // SVG Y axis is flipped, so subtract sin for the upper edge.
      const topX = donutCx + outerR * Math.cos(halfRad);
      const topY = donutCy - outerR * Math.sin(halfRad);
      const botX = donutCx + outerR * Math.cos(-halfRad);
      const botY = donutCy - outerR * Math.sin(-halfRad);
      const stackX = sRect.left - bRect.left;
      const stackTopY = sRect.top - bRect.top;
      const stackBotY = sRect.bottom - bRect.top;
      setConnectorGeom({
        w: bRect.width,
        h: bRect.height,
        top: { x1: topX, y1: topY, x2: stackX, y2: stackTopY },
        bot: { x1: botX, y1: botY, x2: stackX, y2: stackBotY },
      });
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(donutEl);
    ro.observe(stackEl);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [open, otherAngleDeg]);

  if (!open || !calc) return null;

  const donutData = [
    ...mainSlices.map((s) => ({ ...s, isOther: false })),
    ...(otherTotal > 0
      ? [
          {
            name: "Other",
            amount: otherTotal,
            pct: (otherTotal / grandTotal) * 100,
            isOther: true,
          },
        ]
      : []),
  ];

  const body = (
    <>
        {/* Body — 2 columns: donut+legend (flex-1) · Other Breakdown (fixed).
            Positioned relative so the connector-line SVG overlay can span
            both columns and draw the "explosion" affordance from the Other
            wedge (rotated to 3 o'clock) to the stacked column. */}
        <div
          className="flex-1 min-h-0 flex"
          style={{ padding: isPanel ? "4px 18px 12px 6px" : 16, gap: isPanel ? 28 : 24, position: "relative" }}
          ref={bodyRef}
          data-testid="spending-mix-body"
        >
          {/* Explosion affordance — two thin angled lines fanning from the
              Other wedge's top and bottom edges (rotated to 3 o'clock) out
              to the corners of the stacked column on the right. Purely
              decorative: pointer-events: none so nothing steals mouse events
              from the underlying donut or list. */}
          {!isPanel && connectorGeom && otherItems.length > 0 && (
            <svg
              width={connectorGeom.w}
              height={connectorGeom.h}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                pointerEvents: "none",
                overflow: "visible",
                zIndex: 5,
              }}
              data-testid="spending-mix-explosion-lines"
            >
              <line
                x1={connectorGeom.top.x1}
                y1={connectorGeom.top.y1}
                x2={connectorGeom.top.x2}
                y2={connectorGeom.top.y2}
                stroke="#9AA9B4"
                strokeWidth={1.25}
                strokeLinecap="round"
                strokeDasharray="4 3"
              />
              <line
                x1={connectorGeom.bot.x1}
                y1={connectorGeom.bot.y1}
                x2={connectorGeom.bot.x2}
                y2={connectorGeom.bot.y2}
                stroke="#9AA9B4"
                strokeWidth={1.25}
                strokeLinecap="round"
                strokeDasharray="4 3"
              />
            </svg>
          )}
          {/* Left column — donut on top, legend below */}
          <div className={"flex-1 min-w-0 flex " + (isPanel ? "flex-row items-center gap-3" : "flex-col")} data-testid="spending-mix-left">
            <div
              style={isPanel ? { position: "relative", position: "absolute", left: PANEL_DONUT_LEFT, top: PANEL_DONUT_TOP, width: panelDonutPx, height: panelDonutPx, flexShrink: 0 } : { position: "relative", height: "60%", minHeight: 240 }}
              ref={donutRef}
            >
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="amount"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={isPanel ? "61%" : "55%"}
                    outerRadius={isPanel ? "97%" : "88%"}
                    startAngle={pieStartAngle}
                    endAngle={pieEndAngle}
                    paddingAngle={0.5}
                    stroke="#fff"
                    strokeWidth={0.75}
                    isAnimationActive={false}
                    labelLine={false}
                  >
                    {donutData.map((d, idx) => (
                      <Cell
                        key={d.name}
                        fill={d.isOther ? OTHER_COLOR : SLICE_COLORS[idx % SLICE_COLORS.length]}
                        style={d.isOther && !isPanel ? undefined : { cursor: "pointer" }}
                        onClick={d.isOther ? (isPanel ? () => setOtherOpen(true) : undefined) : () => setDrillGroup(d.name)}
                        data-testid={d.isOther ? "spending-mix-other-wedge" : `spending-mix-slice-${idx}`}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={
                      <DelayedTooltipContent
                        formatter={(v, _n, entry) => [
                          `${fmtCurrency(v)}  ·  ${fmtPct1(entry.payload.pct)}${entry.payload.isOther ? (isPanel ? "  ·  Click for details" : "") : "  ·  Click to see elements"}`,
                          entry.payload.name,
                        ]}
                        contentStyle={{
                          background: "#fff",
                          border: "1px solid #1C4E80",
                          borderRadius: 3,
                          fontSize: 11,
                        }}
                      />
                    }
                    wrapperStyle={{ pointerEvents: "none" }}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Center label — total spending in the donut hole */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
                data-testid="spending-mix-center"
              >
                <div className={(isPanel ? "text-[6.5px]" : "text-[9.5px]") + " tracking-[0.14em] uppercase font-bold text-slate"}>
                  Total Spending
                </div>
                <div className={(isPanel ? "text-[10.5px]" : "text-[22px]") + " font-bold font-mono tabular-nums text-navy"}>
                  {fmtCurrency(grandTotal)}
                </div>
                <div className={(isPanel ? "text-[7px]" : "text-[10.5px]") + " text-slate mt-0.5"}>
                  over {horizon} years
                </div>
              </div>
            </div>

            {/* Main legend — full slice list. Single vertical column so
                the legend consumes VERTICAL space (and scrolls) instead
                of spreading horizontally into the "Other" affordance's
                territory. `maxWidth` caps how far right the % and $
                values can travel, keeping them close to the element
                description. Dave 2026-02-08. */}
            <div
              className={(isPanel ? "gap-y-0.5 gap-x-2 leading-tight" : "mt-3 mx-auto gap-y-1 gap-x-3") + " grid w-fit overflow-auto pr-1"}
              style={{
                gridTemplateColumns: "auto minmax(0, max-content) auto auto",
                ...(isPanel ? { position: "absolute", right: 18, bottom: 8, maxWidth: 284 } : { maxWidth: 600 }),
              }}
              ref={legendRef}
              data-testid="spending-mix-legend"
            >
              {donutData.map((d, idx) => {
                const otherClick = d.isOther && isPanel && otherItems.length > 0;
                const clickable = otherClick || !d.isOther;
                return (
                <div
                  key={d.name}
                  className={"grid grid-cols-subgrid col-span-4 items-center text-ink " + (isPanel ? "text-[9.5px]" : "text-[12px]") + (clickable ? " cursor-pointer hover:bg-navy-wash rounded-[2px]" : "")}
                  onClick={otherClick ? () => setOtherOpen(true) : !d.isOther ? () => setDrillGroup(d.name) : undefined}
                  title={otherClick ? `Click to see the ${otherItems.length} ${byGroup ? "groups" : "elements"} in Other` : !d.isOther ? `Click to see the elements in ${d.name}` : undefined}
                  data-testid={`spending-mix-legend-${idx}`}
                >
                  <span
                    style={{
                      width: isPanel ? 10 : 12,
                      height: isPanel ? 10 : 12,
                      background: d.isOther
                        ? OTHER_COLOR
                        : SLICE_COLORS[idx % SLICE_COLORS.length],
                      display: "inline-block",
                      flexShrink: 0,
                    }}
                  />
                  <span className={"min-w-0 truncate " + (d.isOther ? "italic" : "")}>
                    {d.name}
                    {otherClick && <span className="not-italic text-navy font-semibold ml-1" data-testid="spending-mix-other-hint">details ›</span>}
                  </span>
                  <span className="font-mono tabular-nums text-slate text-right">
                    {fmtPct1(d.pct)}
                  </span>
                  <span className={"font-mono tabular-nums text-right " + (isPanel ? "pl-1" : "pl-2") + (isPanel ? "" : " font-semibold")}>
                    {fmtCurrency(d.amount)}
                  </span>
                </div>
                );
              })}
            </div>
          </div>

          {/* Right column — Other Breakdown (modal only; the panel uses the click-in popup). */}
          {!isPanel && (
          <div
            className="border-l border-rule pl-5 flex flex-col"
            style={{ width: 420 }}
            data-testid="spending-mix-other-panel"
          >
            <div className={(isPanel ? "text-[9px]" : "text-[11px]") + " tracking-[0.14em] uppercase font-bold text-navy pb-2 border-b border-rule"}>
              Other Breakdown
            </div>
            {otherItems.length === 0 ? (
              <div className={(isPanel ? "text-[10px]" : "text-[12px]") + " mt-3 text-slate italic"}>
                No {byGroup ? "groups" : "elements"} below {OTHER_THRESHOLD_PCT}% — everything is charted as its own slice.
              </div>
            ) : (
              <>
                <div className={(isPanel ? "text-[8.5px]" : "text-[10.5px]") + " text-slate italic mt-1 mb-2"}>
                  {byGroup ? "Groups" : "Elements"} under {OTHER_THRESHOLD_PCT}% of total spending
                  ({otherItems.length} {byGroup ? (otherItems.length === 1 ? "group" : "groups") : (otherItems.length === 1 ? "element" : "elements")} · {fmtCurrency(otherTotal)} total)
                </div>
                <div className="flex-1 min-h-0 flex gap-3">
                  {/* Vertical stacked column — one segment per Other element,
                      sized proportional to its amount. Largest at top so the
                      visual order matches the list to the right. */}
                  {!isPanel && (
                  <div
                    className="flex flex-col"
                    style={{ width: isPanel ? 26 : 34, minWidth: isPanel ? 26 : 34, border: "1px solid #E2E8F0" }}
                    data-testid="spending-mix-other-stack"
                    ref={stackRef}
                    title={`Stacked mix of ${otherItems.length} elements totalling ${fmtCurrency(otherTotal)}`}
                  >
                    {otherItems.map((it, idx) => (
                      <div
                        key={`bar-${it.name}`}
                        data-testid={`spending-mix-other-seg-${idx}`}
                        title={`${it.name} — ${fmtPct1(it.pct)} — ${fmtCurrency(it.amount)}`}
                        style={{
                          flex: it.amount,
                          background: OTHER_SEG_COLORS[idx % OTHER_SEG_COLORS.length],
                          borderBottom:
                            idx === otherItems.length - 1
                              ? "none"
                              : "1px solid rgba(255,255,255,0.4)",
                          minHeight: 2,
                        }}
                      />
                    ))}
                  </div>
                  )}
                  {/* Itemized list — each row's height is proportional to
                      its amount (matching the stacked column's segment
                      flex) so each list row VERTICALLY ALIGNS with the
                      segment it describes. `minHeight` on each row keeps
                      text legible; if the sum of min-heights overflows
                      the container the list scrolls (segments stay in
                      lockstep with visible rows). Dave 2026-02-08. */}
                  <div
                    className="flex-1 flex flex-col overflow-auto pr-1"
                    data-testid="spending-mix-other-list"
                  >
                    {otherItems.map((it, idx) => (
                      <div
                        key={it.name}
                        onClick={() => setDrillGroup(it.name)}
                        title={`Click to see the elements in ${it.name}`}
                        className={"flex items-center gap-2 text-ink border-b border-rule/70 px-0.5 cursor-pointer hover:bg-navy-wash " + (isPanel ? "text-[9.5px]" : "text-[11.5px]")}
                        style={isPanel ? { minHeight: 22 } : { flex: it.amount, minHeight: 22 }}
                        data-testid={`spending-mix-other-item-${idx}`}
                      >
                        <span
                          style={{
                            width: isPanel ? 8 : 10,
                            height: isPanel ? 8 : 10,
                            background: OTHER_SEG_COLORS[idx % OTHER_SEG_COLORS.length],
                            display: "inline-block",
                            flexShrink: 0,
                          }}
                        />
                        <span className="truncate" title={it.name} style={{ width: `${Math.min(otherNameCh, 30) * 0.5}em`, flexShrink: 1, minWidth: 0 }}>
                          {it.name}
                        </span>
                        <span className={"font-mono tabular-nums text-slate text-right " + (isPanel ? "w-[34px]" : "w-[44px]")}>
                          {fmtPct1(it.pct)}
                        </span>
                        <span className={"font-mono tabular-nums font-semibold text-right " + (isPanel ? "w-[60px]" : "w-[84px]")}>
                          {fmtCurrency(it.amount)}
                        </span>
                        <span className="flex-1" />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          )}
        </div>
        {isPanel && otherOpen && otherItems.length > 0 && (
          <OtherPopup
            items={otherItems}
            total={otherTotal}
            byGroup={byGroup}
            onClose={() => setOtherOpen(false)}
          />
        )}
        {drillGroup && groupElements.get(drillGroup) && (() => {
          const items = groupElements.get(drillGroup);
          const gTotal = items.reduce((s, it) => s + it.amount, 0);
          return (
            <OtherPopup
              items={items}
              title={`${drillGroup} — Elements`}
              subtitle={`${items.length} ${items.length === 1 ? "element" : "elements"} · ${fmtCurrency(gTotal)} over ${horizon} years · % of group`}
              testid="spending-mix-group-popup"
              onClose={() => setDrillGroup(null)}
            />
          );
        })()}
    </>
  );

  if (isPanel) {
    return (
      <div className="panel-compact flex flex-col" style={{ height: "100%", minHeight: 0, position: "relative" }} data-testid="spending-mix-panel">
        <div className="dash-panel-header">
          <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">Reserve Spending Mix</div>
          <div className="text-[10.5px] text-navy font-semibold font-mono tabular-nums">{horizon} Year Horizon</div>
        </div>
        {body}
      </div>
    );
  }

  return (
    <div
      className="chart-modal-overlay fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(20, 30, 45, 0.55)", backdropFilter: "blur(2px)", "--qc-lens-top": `${qcLensTop}px` }}
      onClick={onClose}
      data-testid="spending-mix-overlay"
    >
      <div
        className="chart-modal-content bg-white shadow-2xl flex flex-col"
        style={{
          width: "min(1440px, 95vw)",
          height: "min(820px, 92vh)",
          borderRadius: 8,
          border: "1px solid #1C4E80",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="spending-mix-modal"
      >
        {/* Header — see FundingLevelAnalysisChart for full rationale.
            3-col grid: title left, association centered, horizon right-
            aligned to graph right edge with close X beside it. Meta text
            bumped 11→12 px, bold, and darkened (slate → grey). */}
        <div
          className="grid items-center px-5 py-3 border-b border-rule"
          style={{ background: "#F7FAFC", gridTemplateColumns: "1fr auto 1fr" }}
        >
          <div className="text-[13px] tracking-[0.14em] uppercase font-bold text-navy justify-self-start">
            Reserve Spending Mix
          </div>
          {association ? (
            <div
              className="text-[15px] font-bold text-grey justify-self-center"
              data-testid="spending-mix-assoc-name"
            >
              {association.name}
            </div>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-3 justify-self-end">
            <span
              className="text-[12px] font-bold text-grey font-mono tabular-nums"
              data-testid="spending-mix-horizon-label"
            >
              {horizon} Year Horizon
            </span>
            <button
              type="button"
              onClick={onClose}
              className="text-slate hover:text-coral transition-colors"
              data-testid="spending-mix-close"
              title="Close (Esc)"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {body}
      </div>
    </div>
  );
}
